/* eslint-disable no-param-reassign */

import { configureScope } from '@sentry/browser';

import { noop, device } from '../utils';

export function makeUser ({
    api, router, connectFrontUrl, onSetupUser, onLogin,
}) {
    return {
        namespaced: true,
        state: {
            user: null,
        },
        getters: {
            isConnected (state) {
                return !!state.user;
            },
            isAdmin (state, getters) {
                return getters.isConnected && /@skeepers\.io$/.test(state.user.email);
            },
        },
        mutations: {
            SET_USER (state, user) {
                state.user = user;
                configureScope((scope) => {
                    scope.setUser(user ? {
                        id: user.id,
                        email: user.email,
                        username: user.displayName,
                    } : null);
                });
            },
        },
        actions: {
            /**
             * Specific post-login user action
             */
            async onLogin ({
                state, dispatch, rootGetters, rootState,
            }) {
                const { user } = state;

                if (user) {
                    console.log(`[User] Welcome, ${user.displayName} !`);
                    await api.linkUser(rootState.solution.id, user);
                    // Track successful logins
                    if (rootGetters['core/shouldGrantAccess'].grant) {
                        await dispatch('tracking/teester/trackEvent', rootGetters['solution/isCasting'] ? 'CASTING_SIGNIN' : 'PLATFORM_SIGNIN', { root: true });
                    }
                    await onLogin(user);
                } else {
                    console.log('[User] Skipping login because user is null');
                }
            },
            /**
             * Common user setup: run setup, then call onSetupUser hook to allow additional setup steps
             */
            async setupUser ({ state, dispatch }) {
                const { user } = state;

                if (user) {
                    await dispatch('project/updateProjects', null, { root: true });
                    // An error is thrown by Apollo if the application is not found (404), but we want to ignore it.
                    await dispatch('casting/updateApplication', null, { root: true }).catch(noop);
                    await onSetupUser(user);
                } else {
                    console.log('[User] Skipping setup because user is null');
                }
            },
            /**
             * Get the current user and store it in state.
             */
            async updateUser ({ commit }) {
                try {
                    const user = await api.getUser();
                    commit('SET_USER', user);
                } catch (err) {
                    console.log(`[User] Encountered an error while retrieving user: ${err.message}. Setting the user to null`);
                    commit('SET_USER', null);
                }
            },
            /**
             * Open the connect front with pre-filled parameters
             */
            openConnectFront ({ rootState }, { path, params }) {
                const destination = new URL(connectFrontUrl);
                const searchParams = {
                    ...params,
                    mode: 'social',
                    solutionId: rootState.solution.id,
                    color: rootState.solution.config.COLOR_PRIMARY,
                    logo: rootState.solution.config.HOME_SIGNIN_LOGO,
                    lng: rootState.solution.language,
                    backgroundImage: device.isDesktop
                        ? rootState.solution.config.HOME_BACKGROUND || rootState.solution.config.HOME_BACKGROUND_MOBILE
                        : rootState.solution.config.HOME_BACKGROUND_MOBILE || rootState.solution.config.HOME_BACKGROUND,
                };

                // Destination
                destination.pathname = path || destination.pathname;

                // Query params
                for (const [key, value] of Object.entries(searchParams)) {
                    if (value) {
                        destination.searchParams.set(key, value);
                    }
                }

                window.location.href = destination.href;
            },
            /**
             * Open the login page
             */
            login ({ dispatch, rootGetters }) {
                const redirectUri = new URL(window.location.href);
                redirectUri.pathname = router.resolve(rootGetters['core/welcome']).href;
                redirectUri.searchParams.set('login', true);

                dispatch('openConnectFront', {
                    path: '/',
                    params: {
                        redirectUri: redirectUri.href,
                    },
                });
            },
            /**
             * Opens the logout page
             */
            logout ({ dispatch, rootGetters }) {
                const redirectUri = new URL(window.location.href);
                redirectUri.pathname = router.resolve(rootGetters['core/home']).href;
                // In case the login param is present in the current url, discard it from the redirection url
                redirectUri.searchParams.delete('login');

                dispatch('openConnectFront', {
                    path: '/logout',
                    params: {
                        redirectUri: redirectUri.href,
                        redirectTimeout: 2000,
                    },
                });
            },
            /**
             * Edit user, then update its info
             */
            async editUser ({ state, rootState }, user) {
                await api.updateUser(state.user.id, user, rootState.solution.language);
            },
            async deleteUser ({ state, rootState }) {
                await api.deleteUser(state.user, rootState.solution.language);
            },
            /**
             * Create new address for user
             */
            async createAddress ({ state }, address) {
                try {
                    await api.createAddress(state.user.id, address);
                } catch (err) {
                    console.error(`[User] Encountered an error while creating an address: ${err.message}.`);
                    throw new Error('Error while creating an address');
                }
            },
            /**
             * Update user's address by id
             */
            async updateAddress (_, address) {
                try {
                    await api.updateAddress(address);
                } catch (err) {
                    console.error(`[User] Encountered an error while updating an address: ${err.message}.`);
                    throw new Error('Error while updating the address');
                }
            },
            /**
             * Update user's address by id
             */
            async deleteAddress (_, id) {
                try {
                    await api.deleteAddress(id);
                } catch (err) {
                    console.error(`[User] Encountered an error while deleting an address: ${err.message}.`);
                    throw new Error('Error while deleting the address');
                }
            },
            /**
             *  Find addresses according to search string
             */
            async searchAddress (_, query) {
                try {
                    return api.searchAddress(query);
                } catch (err) {
                    console.error(`[User] Encountered an error while searching addresses: ${err.message}.`);
                    return [];
                }
            },
        },
    };
}
